import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { fmtEur, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Download, Check, X, PenLine, RotateCcw, MapPin, Building2, Loader2, Eye, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import PdfPreviewModal from "@/components/PdfPreviewModal";

export default function PublicOfferPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [form, setForm] = useState({ nome: "", cognome: "", ruolo: "", email: "", accetta_privacy: false });
  const [rejectMotivo, setRejectMotivo] = useState("");
  const [rejectNome, setRejectNome] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/offers/${token}`);
        setData(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-slate-400"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div></div>;
  if (notFound) return <div className="min-h-screen flex items-center justify-center p-8 text-center"><div><X className="w-16 h-16 text-red-300 mx-auto mb-4" /><h1 className="font-display text-2xl font-bold text-[#0F2C59]">Link non valido o scaduto</h1><p className="text-slate-500 mt-2">Contatta il tuo referente commerciale.</p></div></div>;

  const { offer, cliente, settings } = data;
  const stato = offer.stato;
  const isFinal = stato === "accettata" || stato === "rifiutata";

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      {/* Hero */}
      <header className="bg-[#0F2C59] text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center overflow-hidden">
              <img src="/csc-logo.jpg" alt="CSC" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-tight">{settings.company_name}</div>
              <div className="text-xs text-white/60">{settings.address}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-white/60 font-mono uppercase">Offerta</div>
            <div className="font-display font-bold text-xl">{offer.numero}</div>
          </div>
        </div>
        <div className="h-1 bg-[#FACC15]" />
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Status banner */}
        {stato === "accettata" && (
          <Card className="p-5 border-green-300 bg-green-50" data-testid="accepted-banner">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center shrink-0"><Check className="w-6 h-6" /></div>
              <div className="flex-1">
                <div className="font-display font-semibold text-green-800">Offerta accettata</div>
                <div className="text-sm text-green-700">{fmtDate(offer.accepted_at)} — grazie per la fiducia. Ti contatteremo a breve per l'attivazione del servizio.</div>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 border-green-600 text-green-700 hover:bg-green-100"
                  onClick={() => {
                    const url = `${API_BASE}/public/offers/${token}/certificate`;
                    const w = window.open(url, "_blank");
                    if (!w) window.location.href = url;
                  }}
                  data-testid="pub-download-cert"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" /> Scarica certificato di accettazione firmato
                </Button>
                <Button
                  size="sm"
                  className="mt-3 md:mt-3 md:ml-2 bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white"
                  onClick={() => {
                    const url = `${API_BASE}/public/offers/${token}/contract?format=pdf`;
                    const w = window.open(url, "_blank");
                    if (!w) window.location.href = url;
                  }}
                  data-testid="pub-download-contract"
                >
                  <FileSignature className="w-4 h-4 mr-2" /> Scarica il contratto finale
                </Button>
                <p className="text-xs text-green-700/80 mt-2">
                  Il contratto contiene tutte le condizioni concordate e i tuoi dati aziendali.
                  Puoi firmarlo digitalmente o stamparlo, firmarlo e restituirlo via e-mail a{" "}
                  <a className="underline" href="mailto:commerciale@centrosicurezzaconsulenze.net">
                    commerciale@centrosicurezzaconsulenze.net
                  </a>.
                </p>
              </div>
            </div>
          </Card>
        )}
        {stato === "rifiutata" && (
          <Card className="p-5 border-red-300 bg-red-50" data-testid="rejected-banner">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="w-6 h-6" /></div>
              <div>
                <div className="font-display font-semibold text-red-800">Offerta rifiutata</div>
                <div className="text-sm text-red-700">Restiamo a disposizione per proposte alternative.</div>
              </div>
            </div>
          </Card>
        )}

        <div className="text-center py-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Offerta di consulenza annuale</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0F2C59] tracking-tight mt-2">
            {cliente?.ragione_sociale || "Cliente"}
          </h1>
          <p className="text-slate-500 mt-2">Del {fmtDate(offer.created_at)} · Validità {offer.validita_giorni} giorni</p>
        </div>

        {/* Info recap */}
        <Card className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
          <div>
            <div className="text-xs uppercase text-slate-500 font-mono flex items-center gap-1"><Building2 className="w-3 h-3" /> Destinatario</div>
            <div className="font-medium mt-1">{cliente?.ragione_sociale}</div>
            {cliente?.partita_iva && <div className="text-sm text-slate-500 font-mono">P.IVA {cliente.partita_iva}</div>}
            <div className="text-sm text-slate-500">{cliente?.indirizzo} {cliente?.cap} {cliente?.comune} {cliente?.provincia && `(${cliente.provincia})`}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 font-mono flex items-center gap-1"><MapPin className="w-3 h-3" /> Emittente</div>
            <div className="font-medium mt-1">{settings.company_name}</div>
            <div className="text-sm text-slate-500">{settings.address}</div>
            <div className="text-sm text-slate-500">{settings.email} · {settings.phone}</div>
          </div>
        </Card>

        {/* Lines */}
        <Card className="p-6">
          <div className="font-display font-semibold text-[#0F2C59] text-lg mb-4">Servizi inclusi nel canone</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-300">
                <tr>
                  <th className="text-left py-2">Servizio</th>
                  <th className="text-right py-2">Q.tà</th>
                  <th className="text-right py-2">Prezzo</th>
                  <th className="text-right py-2">Totale</th>
                </tr>
              </thead>
              <tbody>
                {(offer.lines || []).filter(l => !l.escludi_pdf).map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <td className="py-3">
                      <div className="font-medium">{l.nome}</div>
                      {l.descrizione && <div className="text-xs text-slate-500 mt-0.5">{l.descrizione}</div>}
                    </td>
                    <td className="text-right font-mono py-3">{l.quantita} {l.unita}</td>
                    <td className="text-right font-mono py-3">{fmtEur(l.prezzo_unitario)}</td>
                    <td className="text-right font-mono py-3">{fmtEur(l.totale)}</td>
                  </tr>
                ))}
                {(offer.accessori || []).filter(a => a.totale > 0).map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 text-slate-600 text-sm">
                    <td className="py-2 italic">{a.descrizione}</td>
                    <td className="text-right font-mono py-2">{a.quantita}</td>
                    <td className="text-right font-mono py-2">{fmtEur(a.prezzo_unitario)}</td>
                    <td className="text-right font-mono py-2">{fmtEur(a.totale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Totali */}
        <Card className="p-0 overflow-hidden">
          <div className="p-6 space-y-2 bg-white">
            <div className="flex justify-between text-sm text-slate-600"><span>Subtotale</span><span className="font-mono">{fmtEur(offer.subtotale)}</span></div>
            {offer.sconto_totale > 0 && <div className="flex justify-between text-sm text-slate-600"><span>Sconto</span><span className="font-mono">-{fmtEur(offer.sconto_totale)}</span></div>}
            <div className="flex justify-between text-sm text-slate-600"><span>Imponibile</span><span className="font-mono">{fmtEur(offer.imponibile)}</span></div>
            <div className="flex justify-between text-sm text-slate-600"><span>IVA {offer.applica_iva ? `(${offer.iva_globale}%)` : ""}</span><span className="font-mono">{fmtEur(offer.iva_totale)}</span></div>
          </div>
          <div className="p-6 bg-[#0F2C59] text-white flex justify-between items-center">
            <div>
              <div className="text-xs uppercase text-white/60 font-mono">Totale annuale</div>
              <div className="font-display text-3xl font-bold mt-1">{fmtEur(offer.totale)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase text-white/60 font-mono">Equivalente mensile</div>
              <div className="text-xl font-mono font-bold text-[#FACC15] mt-1">{fmtEur(offer.valore_mensile)}</div>
            </div>
          </div>
        </Card>

        {/* Condizioni */}
        {(offer.condizioni || offer.modalita_pagamento || offer.durata_incarico || offer.esclusioni || offer.note_visibili) && (
          <Card className="p-6 space-y-3">
            <div className="font-display font-semibold text-[#0F2C59] text-lg">Condizioni commerciali</div>
            <div className="text-sm text-slate-700 space-y-1">
              {offer.modalita_pagamento && <div><strong>Modalità di pagamento:</strong> {offer.modalita_pagamento}</div>}
              {offer.durata_incarico && <div><strong>Durata incarico:</strong> {offer.durata_incarico}</div>}
              {offer.esclusioni && <div><strong>Esclusioni:</strong> {offer.esclusioni}</div>}
              {offer.note_visibili && <div className="pt-2 border-t border-slate-200 mt-2 whitespace-pre-line">{offer.note_visibili}</div>}
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 justify-center pt-4">
          <Button variant="outline" size="lg" onClick={() => {
            const url = `${API_BASE}/public/offers/${token}/pdf?inline=true`;
            const w = window.open(url, "_blank");
            if (!w) window.location.href = url;
          }} data-testid="pub-preview-pdf">
            <Eye className="w-4 h-4 mr-2" /> Anteprima PDF
          </Button>
          <Button variant="outline" size="lg" onClick={() => {
            const url = `${API_BASE}/public/offers/${token}/pdf`;
            const w = window.open(url, "_blank");
            if (!w) window.location.href = url;
          }} data-testid="pub-download-pdf">
            <Download className="w-4 h-4 mr-2" /> Scarica PDF
          </Button>
          {!isFinal && (
            <>
              <Button size="lg" className="bg-green-600 hover:bg-green-700" onClick={() => setAcceptOpen(true)} data-testid="pub-accept-btn">
                <Check className="w-5 h-5 mr-2" /> Accetta offerta
              </Button>
              <Button variant="outline" size="lg" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setRejectOpen(true)} data-testid="pub-reject-btn">
                <X className="w-4 h-4 mr-2" /> Rifiuta
              </Button>
            </>
          )}
        </div>

        <div className="text-center text-xs text-slate-400 pt-8 pb-4">
          {settings.company_name} · Documento generato da PreventivatoreCSC
        </div>
      </main>

      {/* Accept Dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0F2C59] font-display text-2xl">Accetta l'offerta {offer.numero}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5" data-testid="acc-nome" /></div>
              <div><Label>Cognome *</Label><Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} className="mt-1.5" data-testid="acc-cognome" /></div>
            </div>
            <div><Label>Ruolo in azienda</Label><Input value={form.ruolo} onChange={(e) => setForm({ ...form, ruolo: e.target.value })} className="mt-1.5" placeholder="es. Amministratore, Titolare" data-testid="acc-ruolo" /></div>
            <div>
              <Label>E-mail *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5"
                placeholder="La sua email per ricevere il contratto firmato"
                data-testid="acc-email"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Su questa e-mail le invieremo automaticamente il contratto definitivo in PDF, subito dopo l'accettazione.
              </p>
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2"><PenLine className="w-4 h-4" /> Firma grafica *</Label>
              <SignaturePad />
              <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                Disegnando la tua firma qui sopra e cliccando "Conferma", stai apponendo una firma
                elettronica ai sensi dell'art. 20 del CAD (D.Lgs. 82/2005). Vengono registrati
                automaticamente: nome, ruolo, timestamp UTC, indirizzo IP e user-agent del dispositivo.
                Riceverai un certificato PDF con codice di verifica SHA-256.
              </p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700">
              <Checkbox checked={form.accetta_privacy} onCheckedChange={(v) => setForm({ ...form, accetta_privacy: !!v })} data-testid="acc-privacy" />
              <span>Accetto le condizioni commerciali e il trattamento dei dati personali ai sensi del Reg. UE 2016/679.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>Annulla</Button>
            <Button
              disabled={accepting || !form.nome || !form.cognome || !form.email || !form.accetta_privacy}
              onClick={async () => {
                setAccepting(true);
                try {
                  const sig = document.getElementById("sig-pad")?.__dataURL || null;
                  await axios.post(`${API_BASE}/public/offers/${token}/accept`, { ...form, firma_base64: sig });
                  toast.success("Offerta accettata! Grazie.");
                  setAcceptOpen(false);
                  setTimeout(() => window.location.reload(), 800);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore invio");
                } finally { setAccepting(false); }
              }}
              className="bg-green-600 hover:bg-green-700"
              data-testid="acc-confirm-btn"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Conferma accettazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rifiuta offerta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Il tuo nome</Label><Input value={rejectNome} onChange={(e) => setRejectNome(e.target.value)} className="mt-1.5" data-testid="rej-nome" /></div>
            <div><Label>Motivo (opzionale)</Label><Textarea rows={3} value={rejectMotivo} onChange={(e) => setRejectMotivo(e.target.value)} className="mt-1.5" data-testid="rej-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Annulla</Button>
            <Button
              onClick={async () => {
                try {
                  await axios.post(`${API_BASE}/public/offers/${token}/reject`, { nome: rejectNome, motivo: rejectMotivo });
                  toast.success("Offerta rifiutata");
                  setRejectOpen(false);
                  setTimeout(() => window.location.reload(), 700);
                } catch (e) { toast.error("Errore"); }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="rej-confirm-btn"
            >Conferma rifiuto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview modal */}
      <PdfPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        filename={`${offer.numero}.pdf`}
        title={`Offerta ${offer.numero}`}
        fetchBlob={async () => {
          const r = await axios.get(`${API_BASE}/public/offers/${token}/pdf`, { responseType: "blob" });
          return new Blob([r.data], { type: "application/pdf" });
        }}
      />
    </div>
  );
}

// ---- Simple signature pad (canvas, mouse + touch) ----
function SignaturePad() {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { drawing.current = true; last.current = getPoint(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#0F2C59";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    // Store dataURL
    canvasRef.current.__dataURL = canvasRef.current.toDataURL("image/png");
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    canvasRef.current.__dataURL = null;
  };

  return (
    <div>
      <canvas
        id="sig-pad"
        ref={canvasRef}
        width={440}
        height={140}
        className="w-full border border-slate-300 rounded bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex justify-end mt-1">
        <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="clear-sig-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Cancella firma
        </Button>
      </div>
    </div>
  );
}
